import { useLazyQuery, useMutation, useQuery } from '@apollo/client/react';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import { faCircleCheck, faWarning } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { LocalDate, ZonedDateTime, ZoneId } from '@js-joda/core';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import debounce from 'lodash/debounce';
import flatten from 'lodash/flatten';
import noop from 'lodash/noop';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import {
  AvailabilityCalendarTimeFragment,
  BookSessionDocument,
  CoachBookingDetailsDocument,
  CoachBookingDetailsQueryVariables,
  CoachingStyle,
  CoachSessionStyle,
  CoachSessionSubtype,
  CoachSessionType,
  CoachType,
  GetClientCoachListDocument,
  MemberBookingDetailsFragment,
  MemberPersonalTrainingInfoDocument,
  RebookSessionDocument,
  SelfDocument,
  UserAutocompleteDetailsFragment,
} from '../../graphql/types';
import useLocalStorage from '../../hooks/useLocalStorage';
import { useMemberSearch } from '../../hooks/useMemberSearch';
import useTracking from '../../hooks/useTracking';
import {
  fullDateNoYear,
  getBrowserTimezone,
  getDisplayLabelForTimezoneWithOffset,
  getListOfLocalAvailableTimeZones,
  getLocalTimeUtcEndOfDay,
  getLocalTimeUtcStartOfDay,
} from '../../utils/date';
import {
  EventCategories,
  EventLabels,
  EventNames,
  EventProfileCardNames,
} from '../../utils/trackingEvents';
import LoadingPanel from '../LoadingPanel';
import Modal from '../Modal';
import { IconButton } from '../reusable/Button/IconButton';
import { DateCalendar } from '../reusable/DateCalendar';
import GenericError from '../reusable/GenericError';
import { MemberAutocomplete } from '../reusable/MemberAutocomplete';
import { OptionType, Select } from '../reusable/Select';
import { getSessionSubtypeOption } from '../reusable/Session/util';
import { SnackbarVariant } from '../reusable/Snackbar';
import { useSnackbarContext } from '../reusable/Snackbar/SnackbarContextProvider';
import Text, { TextColor, TextKind } from '../reusable/Text';
import { SessionWithGroupingMetaData } from '../Sessions/util';
import {
  StyledCalendarContainer,
  StyledDurationTitleContainer,
  StyledMainContainer,
  StyledModalContainer,
  StyledSelectContainer,
  StyledSessionTimeContainer,
  StyledSidebar,
  StyledTimeZoneContainer,
} from './BookSessionModalStyles';
import { ListTimesForDay } from './SelectableTimeList';
import { getAvailableCoachesList } from './util';

const getTimesForToday = (
  availability: AvailabilityCalendarTimeFragment[],
  day: LocalDate,
): AvailabilityCalendarTimeFragment[] => {
  const startOfDay = getLocalTimeUtcStartOfDay(day.atStartOfDay());
  const endOfDay = getLocalTimeUtcEndOfDay(day.atStartOfDay());
  const filtered = availability.filter((a) => {
    const parsed = ZonedDateTime.parse(a.start);
    const isAfterStartOfDay = parsed.isAfter(startOfDay);
    const isBeforeEndOfDay = parsed.isBefore(endOfDay);
    return isAfterStartOfDay && isBeforeEndOfDay;
  });
  return filtered;
};

interface BookSessionModalForm {
  coachId: string;
  memberId: string;
  subCategory?: OptionType;
  sessionType: OptionType;
  location: OptionType;
  timezone: OptionType;
  day: LocalDate;
  sessionTime?: ZonedDateTime;
}

const coachingSessionType = {
  label: 'Coaching Session',
  value: CoachSessionType.UserCoachingConsult,
};
const nutritionSessionType = {
  label: 'Nutrition Session',
  value: CoachSessionType.UserNutritionConsult,
};
const personalTrainingSessionType = {
  label: 'Personal Training',
  value: CoachSessionType.PersonalTraining,
};

const getSessionTypeOption = (type: CoachSessionType) => {
  switch (type) {
    case CoachSessionType.UserCoachingConsult:
      return coachingSessionType;
    case CoachSessionType.UserNutritionConsult:
      return nutritionSessionType;
    case CoachSessionType.PersonalTraining:
      return personalTrainingSessionType;
    default:
      return coachingSessionType;
  }
};
const getSessionTypeOptions = (
  type: CoachType,
  clientSupportsPersonalTraining: boolean,
) => {
  if (type === CoachType.Coach) {
    const values = [coachingSessionType];
    if (clientSupportsPersonalTraining) {
      values.push(personalTrainingSessionType);
    }
    return values;
  }
  if (type === CoachType.Dietitian) {
    return [nutritionSessionType];
  }

  return [];
};

const virtualLocation = {
  label: 'Virtual',
  value: CoachSessionStyle.Virtual,
};
const inPersonLocation = {
  label: 'In-Person',
  value: CoachSessionStyle.InPerson,
};
const getLocationDropdownOptions = (style?: CoachingStyle | null) => {
  const values = [virtualLocation];

  if (style === CoachingStyle.Hybrid) {
    values.push(inPersonLocation);
  }
  return values;
};

const subCategoryDefaultOption: OptionType = {
  label: 'Select a sub-category',
  value: undefined,
};

export const getSubCategoryOptions = (sessionType: CoachSessionType) => {
  if (sessionType === CoachSessionType.UserCoachingConsult) {
    return [
      {
        label: 'Movement assessment',
        value: CoachSessionSubtype.MovementAssessment,
      },
      {
        label: 'Ergonomic evaluation',
        value: CoachSessionSubtype.ErgonomicEvaluation,
      },
      {
        label: 'Body composition assessment',
        value: CoachSessionSubtype.BodyCompositionAssessment,
      },
      {
        label: 'New member orientation',
        value: CoachSessionSubtype.NewMemberOrientation,
      },
    ];
  }

  return [];
};

const listOfTimezones = getListOfLocalAvailableTimeZones();

interface Props {
  member?: Pick<
    MemberBookingDetailsFragment,
    'id' | 'firstName' | 'lastName' | 'fitClient'
  >;
  session?: SessionWithGroupingMetaData;
  isOpen?: boolean;
  closeModal?: () => void;
  testId?: string;
  refetchQueries?: string[];
}

function checkIfRescheduling(
  session: SessionWithGroupingMetaData | undefined,
): session is SessionWithGroupingMetaData & { sessionType: CoachSessionType } {
  return Boolean(session !== undefined && session.sessionType !== undefined);
}

type ContentProps = Omit<Props, 'isOpen' | 'testId' | 'closeModal'> & {
  closeModal: () => void;
};

const BookSessionModalContent: React.FC<ContentProps> = ({
  member,
  session,
  refetchQueries = [],
  closeModal,
}) => {
  const isRescheduling = checkIfRescheduling(session);
  const { track } = useTracking();
  const { setSnackbar } = useSnackbarContext();
  const [coachSearchValue, setCoachSearchValue] = useState('');
  const [, setRecentlyScheduled] = useLocalStorage<boolean>(
    `${session?.id}.recentlyScheduled`,
    false,
  );

  const [bookSession] = useMutation(BookSessionDocument);
  const {
    data: selfData,
    loading: selfLoading,
    error: selfError,
  } = useQuery(SelfDocument);
  const [
    fetchCoachBookDetails,
    {
      data: coachBookingDetailsData,
      error: coachBookingDetailsError,
      refetch: refetchCoachBookingDetails,
      loading: coachBookingDetailsLoading,
    },
  ] = useLazyQuery(CoachBookingDetailsDocument);

  const {
    clients,
    loading: userListLoading,
    error: userListError,
    search: handleMemberAutocompleteInput,
  } = useMemberSearch({ skip: isRescheduling, hasSignedIn: true });
  const [
    fetchClientCoachList,
    { data: coachListData, loading: coachListLoading },
  ] = useLazyQuery(GetClientCoachListDocument);

  const [fetchPTInfo] = useLazyQuery(MemberPersonalTrainingInfoDocument);

  const initialSessionType =
    selfData?.self?.coachData?.type === CoachType.Dietitian
      ? nutritionSessionType
      : coachingSessionType;

  const { cronofyTimezone, availability, type, coachingStyle } =
    coachBookingDetailsData?.user?.coachData ?? {};
  // Never fall back to a fixed zone: showing a Central coach times labelled as
  // Eastern reads as legitimate, so prefer the viewer's own zone when the
  // coach's configured zone hasn't loaded (or isn't set).
  const coachTimezone = cronofyTimezone ?? getBrowserTimezone();

  const defaultFormValues = {
    memberId: member?.id ?? '',
    coachId: selfData?.self?.id,
    sessionType: isRescheduling
      ? getSessionTypeOption(session.sessionType)
      : initialSessionType,
    location: session?.location ? inPersonLocation : virtualLocation,
    timezone: {
      label: getDisplayLabelForTimezoneWithOffset(coachTimezone),
      value: coachTimezone,
    },
    subCategory: isRescheduling
      ? getSessionSubtypeOption(session.sessionSubtype)
      : undefined,
    day: LocalDate.now(),
    sessionTime: undefined,
  };

  const methods = useForm<BookSessionModalForm>({
    defaultValues: defaultFormValues,
  });
  const {
    handleSubmit,
    watch,
    formState: { isSubmitting },
  } = methods;

  const availabilityNodes = useMemo(
    () => availability?.edges.map(({ node }) => node) ?? [],
    [availability],
  );
  const coachesLocations =
    coachBookingDetailsData?.user?.coachData?.locations.edges.map(
      ({ node }) => node.id,
    ) ?? [];

  const dayWatcher = watch('day');
  const timezoneWatcher = watch('timezone');
  const memberIdWatcher = watch('memberId');
  const sessionTimeWatcher = watch('sessionTime');
  const coachIdWatcher = watch('coachId');
  const locationWatcher = watch('location');
  const sessionTypeWatcher = watch('sessionType');
  const formValid = memberIdWatcher && coachIdWatcher && sessionTimeWatcher;

  const availableSubcategories = getSubCategoryOptions(
    sessionTypeWatcher.value as CoachSessionType,
  );

  // when availability changes, find the first available day
  useEffect(() => {
    const timezone = {
      label: getDisplayLabelForTimezoneWithOffset(coachTimezone),
      value: coachTimezone,
    };
    const firstAvailableDay = availabilityNodes[0]
      ? ZonedDateTime.parse(availabilityNodes[0].start)
          .withZoneSameInstant(ZoneId.of(timezone.value))
          .toLocalDate()
      : LocalDate.now();
    methods.setValue('day', firstAvailableDay);
    methods.setValue('timezone', timezone);
  }, [availabilityNodes, coachTimezone, methods]);

  const usersList = useMemo(() => {
    const selectedCoachClientIds =
      coachBookingDetailsData?.user?.coachData?.clients.edges.map(
        ({ node }) => node.id,
      );

    // if a coach is selected, make sure they only see members of the same client
    const filteredClients = clients.filter((c) =>
      selectedCoachClientIds ? selectedCoachClientIds.includes(c.id) : true,
    );

    return flatten(
      filteredClients.map((client) =>
        client.users.edges.map(({ node }) => node),
      ),
    );
  }, [clients, coachBookingDetailsData?.user?.coachData?.clients]);

  const availableCoachesList = useMemo(() => {
    if (coachListData && selfData && usersList) {
      return getAvailableCoachesList({
        coachListData,
        selfData,
        usersList,
        memberId: memberIdWatcher,
        coachSearchValue,
      });
    }
    return [];
  }, [coachListData, selfData, memberIdWatcher, usersList, coachSearchValue]);

  const selectedUserSupportsPt =
    usersList.find((u) => u.id === memberIdWatcher)?.fitClient
      ?.usesPersonalTraining ?? false;

  const memberSupportsPt = member?.fitClient?.usesPersonalTraining ?? false;
  const membersClientSupportsPt = selectedUserSupportsPt || memberSupportsPt;

  const fetchCoachBookDetailsWithVariables = useCallback(
    async (variables: Partial<CoachBookingDetailsQueryVariables>) => {
      if (variables.id) {
        const nowZdt = ZonedDateTime.now();
        return fetchCoachBookDetails({
          variables: {
            id: variables.id,
            coachSessionStyle:
              variables.coachSessionStyle ||
              (methods.getValues('location').value as CoachSessionStyle),
            bypassOneDayDelay: true,
            availabilityStart: nowZdt.toString(),
          },
        });
      }
      return null;
    },
    [fetchCoachBookDetails, methods],
  );

  useEffect(() => {
    // on mount, fetch the coach booking details for the selected coach
    if (selfData?.self?.id) {
      fetchCoachBookDetailsWithVariables({ id: selfData?.self?.id });
    }
  }, [fetchCoachBookDetailsWithVariables, selfData?.self?.id]);

  useEffect(() => {
    // on mount, fetch the client list for the selected coach
    // only for booking a new session, we don't need a coach list for rescheduling
    if (!isRescheduling) {
      fetchClientCoachList();
    }
  }, [fetchClientCoachList, isRescheduling]);

  const calendarShouldDisableDate = (value: Dayjs) => {
    const date = new Date(dayjs(value).toString());
    const localDate = LocalDate.of(
      date.getFullYear(),
      date.getMonth() + 1,
      date.getDate(),
    );
    const foundAvailabilityForDay = getTimesForToday(
      availabilityNodes,
      localDate,
    );
    return foundAvailabilityForDay.length === 0;
  };

  const todaysAvailability = useMemo(() => {
    const day = dayWatcher;
    return getTimesForToday(availabilityNodes, day);
  }, [availabilityNodes, dayWatcher]);

  const sessionTypeOptions: OptionType[] = useMemo(() => {
    if (isRescheduling) {
      return [getSessionTypeOption(session.sessionType)];
    }
    if (type) {
      return getSessionTypeOptions(type, membersClientSupportsPt);
    }
    return [coachingSessionType];
  }, [session, type, membersClientSupportsPt, isRescheduling]);

  const handleCoachAutocompleteInput = debounce((value: string) => {
    setCoachSearchValue(value);
    fetchClientCoachList({
      variables: value
        ? {
            coachName: value,
          }
        : {},
    });
  }, 500);
  const closeModalClick = () => {
    closeModal();
    if (isRescheduling) {
      track(EventNames.CloseClick, {
        card_name: EventProfileCardNames.RescheduleSessionModal,
        event_category: EventCategories.Sessions,
        session_id: session.id,
      });
    } else {
      track(EventNames.CloseClick, {
        card_name: EventProfileCardNames.AddSessionModal,
        event_category: EventCategories.Sessions,
      });
    }
  };

  /**
   * set memberId when memebr changes
   * this handles the case when the modal is opened from chat
   * and member is initially null
   */
  useEffect(() => {
    if (member?.id) {
      methods.setValue('memberId', member.id);
    }
  }, [methods, member?.id]);

  /**
   * default the booking coach to the logged in coach once they resolve, for the
   * case where the modal is opened before the self query has come back
   */
  useEffect(() => {
    if (selfData?.self?.id && !methods.getValues('coachId')) {
      methods.setValue('coachId', selfData.self.id);
    }
  }, [methods, selfData?.self?.id]);

  const [rebookSession] = useMutation(RebookSessionDocument);
  const handleRescheduleSession = async (formValues: BookSessionModalForm) => {
    if (isRescheduling && formValues.sessionTime) {
      track(EventNames.ButtonClick, {
        card_name: EventProfileCardNames.RescheduleSessionModal,
        event_category: EventCategories.Sessions,
        event_label: EventLabels.Save,
        session_id: session.id,
      });
      rebookSession({
        refetchQueries,
        variables: {
          sessionId: session.id,
          start: formValues.sessionTime.toString(),
        },
        optimisticResponse: {
          rebookSession: {
            __typename: 'BookSessionPayload',
            session: {
              __typename: 'CoachSession',
              ...session,
              calendar: {
                __typename: 'Calendar',
                start: formValues.sessionTime.toString(),
                end: '',
              },
            },
          },
        },
        onError: () => {
          setSnackbar({
            message: 'Error saving changes',
            icon: faWarning,
            variant: SnackbarVariant.Danger,
          });
        },
      });

      closeModal();
      setSnackbar({
        message: 'Session rescheduled.',
        icon: faCircleCheck,
        variant: SnackbarVariant.Success,
      });
      setRecentlyScheduled(true);
    }
  };

  const handleBooking = async (formValues: BookSessionModalForm) => {
    if (formValues.sessionTime) {
      const sessionType = formValues.sessionType.value as CoachSessionType;
      const response = await bookSession({
        refetchQueries,
        variables: {
          input: {
            coachId: formValues.coachId,
            duration: 30,
            start: formValues.sessionTime.toString(),
            memberId: formValues.memberId,
            sessionType,
            sessionSubtype: formValues.subCategory
              ?.value as CoachSessionSubtype,
            sessionStyle: locationWatcher.value as CoachSessionStyle,
            locationId:
              locationWatcher.value === CoachSessionStyle.InPerson
                ? coachesLocations[0]
                : undefined,
          },
        },
        onCompleted: (createdSessionResponse) => {
          refetchCoachBookingDetails();
          if (sessionType === CoachSessionType.PersonalTraining) {
            fetchPTInfo({ variables: { memberId: formValues.memberId } });
          }
          closeModal();
          setSnackbar({
            message: 'Session booked.',
            icon: faCircleCheck,
            variant: SnackbarVariant.Success,
          });
          setRecentlyScheduled(
            true,
            `${createdSessionResponse.bookSession.session.id}.recentlyScheduled`,
          );
        },
        onError: (e) => {
          if (e.message === 'Member has no personal training credits') {
            return setSnackbar({
              message: 'Member has no personal training credits',
              icon: faWarning,
              variant: SnackbarVariant.Danger,
            });
          }

          if (e.message === 'The chosen time is not available') {
            return setSnackbar({
              message: 'The chosen time is no longer available',
              icon: faWarning,
              variant: SnackbarVariant.Danger,
            });
          }

          return setSnackbar({
            message: 'Error saving changes',
            icon: faWarning,
            variant: SnackbarVariant.Danger,
          });
        },
      });
      if (response.data?.bookSession.session.id) {
        track(EventNames.ButtonClick, {
          card_name: EventProfileCardNames.AddSessionModal,
          event_category: EventCategories.Sessions,
          event_label: EventLabels.BookSession,
          session_id: response.data.bookSession.session.id,
        });
      }
    }
  };

  const handleCoachSelection = async (
    user: UserAutocompleteDetailsFragment | null,
  ) => {
    setCoachSearchValue(user ? `${user.firstName} ${user.lastName}` : '');
    methods.setValue('sessionTime', undefined);

    if (!user) {
      return;
    }

    const { data: selectedCoachBookingDetailsData } =
      (await fetchCoachBookDetailsWithVariables({ id: user?.id })) || {};

    if (selectedCoachBookingDetailsData?.user && !isRescheduling) {
      const defaultSessionType =
        selectedCoachBookingDetailsData.user.coachData?.type === CoachType.Coach
          ? coachingSessionType
          : nutritionSessionType;

      methods.setValue('sessionType', defaultSessionType);
    }

    if (
      selectedCoachBookingDetailsData?.user?.coachData?.coachingStyle ===
      CoachingStyle.Virtual
    ) {
      methods.setValue('location', virtualLocation);
    }

    // refetch unfiltered coach list
    await fetchClientCoachList();
  };

  const availabilityDataMissing =
    !coachBookingDetailsLoading && availability === undefined;
  const criticalDataMissing =
    selfError ||
    userListError ||
    coachBookingDetailsError ||
    availabilityDataMissing;

  const coachBookingDetailsPending =
    Boolean(selfData?.self?.id) &&
    !coachBookingDetailsData &&
    !coachBookingDetailsError;
  const initialLoading = selfLoading || coachBookingDetailsPending;

  return (
    <>
      {initialLoading && (
        <StyledModalContainer style={{ gridTemplateColumns: '1fr' }}>
          <LoadingPanel
            noBorder
            height={564}
            testId="bookSessionModal-loading"
          />
        </StyledModalContainer>
      )}
      {criticalDataMissing && !initialLoading && (
        <GenericError
          testId="bookSessionsModal-error"
          error={selfError || userListError || coachBookingDetailsError}
        />
      )}
      {!criticalDataMissing && !initialLoading && (
        <FormProvider {...methods}>
          <form
            onSubmit={handleSubmit(
              isRescheduling ? handleRescheduleSession : handleBooking,
            )}
            data-testid="bookSessionModal-form"
          >
            <div style={{ position: 'absolute', top: '8px', right: '8px' }}>
              <IconButton
                ariaLabel="Close session booking"
                onClick={closeModalClick}
                icon={<FontAwesomeIcon icon={faXmark} fontSize={24} />}
                testId="bookSessionModal-close"
              />
            </div>
            <StyledModalContainer>
              <StyledSidebar>
                <Text kind={TextKind.TitleM}>
                  {session ? 'Reschedule' : 'Add'} Session
                </Text>
                <Controller
                  name="memberId"
                  render={({ field }) => (
                    <MemberAutocomplete
                      name={field.name}
                      selectedUserId={field.value}
                      loading={userListLoading}
                      disabled={isRescheduling || Boolean(member)}
                      onInputChange={handleMemberAutocompleteInput}
                      onSelection={(user) => {
                        field.onChange(user?.id ?? '');
                      }}
                      users={usersList}
                      trackingData={{
                        card_name: EventProfileCardNames.AddSessionModal,
                        event_category: EventCategories.Sessions,
                        event_label: EventLabels.Member,
                      }}
                    />
                  )}
                />
                <Controller
                  name="coachId"
                  render={({ field }) => (
                    <MemberAutocomplete
                      label="Coach"
                      placeholder="Select a coach"
                      name={field.name}
                      selectedUserId={field.value}
                      disabled={isRescheduling || coachBookingDetailsLoading}
                      users={availableCoachesList ?? []}
                      loading={coachListLoading || coachBookingDetailsLoading}
                      onInputChange={handleCoachAutocompleteInput}
                      onSelection={async (user) => {
                        field.onChange(user?.id || '');

                        await handleCoachSelection(user);
                      }}
                      trackingData={{
                        card_name: EventProfileCardNames.AddSessionModal,
                        event_category: EventCategories.Sessions,
                        event_label: EventLabels.Coach,
                      }}
                    />
                  )}
                />
                <Controller
                  name="sessionType"
                  render={({ field }) => (
                    <StyledSelectContainer>
                      <Text kind={TextKind.LabelL}>Session Type</Text>
                      <Select
                        fullWidth
                        disabled={isRescheduling}
                        name={field.name}
                        value={field.value}
                        options={sessionTypeOptions}
                        onChange={(option) => {
                          field.onChange(option);
                          track(EventNames.DropDownMenuClick, {
                            card_name: EventProfileCardNames.AddSessionModal,
                            event_category: EventCategories.Sessions,
                            event_label: EventLabels.SessionType,
                          });
                          if (
                            option.value !==
                            CoachSessionType.UserCoachingConsult
                          ) {
                            methods.setValue('subCategory', undefined);
                          }
                        }}
                      />
                    </StyledSelectContainer>
                  )}
                />
                {availableSubcategories.length !== 0 && (
                  <Controller
                    name="subCategory"
                    render={({ field }) => (
                      <StyledSelectContainer>
                        <div style={{ display: 'flex', gap: 4 }}>
                          <Text kind={TextKind.LabelL}>Sub-category</Text>
                          <Text
                            kind={TextKind.BodyS}
                            color={TextColor.Secondary}
                          >
                            (Optional)
                          </Text>
                        </div>
                        <Select
                          fullWidth
                          disabled={isRescheduling}
                          name={field.name}
                          clearable
                          onClear={() => {
                            field.onChange(subCategoryDefaultOption);
                            track(EventNames.DropDownMenuCleared, {
                              event_category: EventCategories.Sessions,
                              card_name: EventProfileCardNames.AddSessionModal,
                            });
                          }}
                          value={field.value || subCategoryDefaultOption}
                          options={availableSubcategories}
                          onOpenCallback={() => {
                            track(EventNames.DropDownMenuClick, {
                              card_name: EventProfileCardNames.AddSessionModal,
                              event_category: EventCategories.Sessions,
                              event_label: EventLabels.SubCategory,
                            });
                          }}
                          onChange={(option) => {
                            field.onChange(option);
                          }}
                        />
                      </StyledSelectContainer>
                    )}
                  />
                )}
                <Controller
                  name="location"
                  render={({ field }) => (
                    <StyledSelectContainer>
                      <Text kind={TextKind.LabelL}>Location</Text>
                      <Select
                        fullWidth
                        disabled={isRescheduling}
                        name={field.name}
                        value={field.value}
                        options={getLocationDropdownOptions(coachingStyle)}
                        onChange={async (option) => {
                          field.onChange(option);
                          track(EventNames.DropDownMenuClick, {
                            card_name: EventProfileCardNames.AddSessionModal,
                            event_category: EventCategories.Sessions,
                            event_label: EventLabels.Location,
                          });
                          await fetchCoachBookDetailsWithVariables({
                            id: coachIdWatcher,
                            coachSessionStyle:
                              option.value as CoachSessionStyle,
                          });
                        }}
                      />
                    </StyledSelectContainer>
                  )}
                />
              </StyledSidebar>
              <StyledMainContainer>
                <div>
                  <Text kind={TextKind.TitleS}>
                    Pick a {isRescheduling ? 'new' : ''} date and time
                  </Text>
                  <StyledCalendarContainer>
                    <div>
                      <StyledTimeZoneContainer>
                        <Text kind={TextKind.LabelL}>Time Zone: </Text>{' '}
                        <Controller
                          name="timezone"
                          render={({ field }) => (
                            <Select
                              fullWidth
                              name={field.name}
                              value={
                                coachBookingDetailsLoading
                                  ? {
                                      label: 'Loading...',
                                      value: undefined,
                                    }
                                  : field.value
                              }
                              options={listOfTimezones}
                              onChange={(option) => {
                                field.onChange(option);
                              }}
                            />
                          )}
                        />
                      </StyledTimeZoneContainer>
                      <Controller
                        name="day"
                        render={({ field }) => (
                          <div data-testid="bookSessionModal-calendar">
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                              <DateCalendar
                                disablePast
                                value={dayjs(field.value.toString())}
                                shouldDisableDate={calendarShouldDisableDate}
                                onChange={(value) => {
                                  const date = new Date(
                                    dayjs(value).toString(),
                                  );
                                  const localDate = LocalDate.of(
                                    date.getFullYear(),
                                    date.getMonth() + 1,
                                    date.getDate(),
                                  );
                                  field.onChange(localDate);
                                  methods.setValue('sessionTime', undefined);
                                }}
                              />
                            </LocalizationProvider>
                          </div>
                        )}
                      />
                    </div>
                    <StyledSessionTimeContainer>
                      <StyledDurationTitleContainer>
                        <Text kind={TextKind.LabelL}>Duration:</Text>{' '}
                        <Text kind={TextKind.BodyM}>30 minutes</Text>
                      </StyledDurationTitleContainer>
                      <Text kind={TextKind.TitleXs}>
                        {fullDateNoYear(dayWatcher)}
                      </Text>

                      <Controller
                        name="sessionTime"
                        render={({ field }) => (
                          <ListTimesForDay
                            loading={coachBookingDetailsLoading}
                            timezone={timezoneWatcher.value as string}
                            availability={todaysAvailability}
                            selectedTime={field.value}
                            onChange={(selectedTime) => {
                              field.onChange(selectedTime);
                            }}
                          />
                        )}
                      />
                    </StyledSessionTimeContainer>
                  </StyledCalendarContainer>
                </div>
                <Modal.Actions
                  confirmButton={{
                    label: isRescheduling ? 'Save Changes' : 'Book Session',
                    type: 'submit',
                    disabled: isSubmitting || !formValid,
                  }}
                />
              </StyledMainContainer>
            </StyledModalContainer>
          </form>
        </FormProvider>
      )}
    </>
  );
};

export const BookSessionModal: React.FC<Props> = ({
  isOpen = false,
  closeModal = noop,
  testId,
  ...contentProps
}) => (
  <Modal isOpen={isOpen} onClose={closeModal} testId={testId} width="1022px">
    <BookSessionModalContent {...contentProps} closeModal={closeModal} />
  </Modal>
);
